import { useEffect, useMemo, useState } from "react";
import {
  evaluateContractOwnershipAction,
  executeContractOwnershipAction,
  listCustomers,
  type AdminSession,
  type ContractOwnershipCheck,
  type CustomerListItem,
} from "@/lib/api";

type ContractSummary = {
  id: string;
  contractNumber: string;
  direccion: string;
  sector: string;
};

type Props = {
  session: AdminSession;
  contract: ContractSummary;
  currentCustomerId: string;
  currentCustomerName: string;
  initialAction?: "deactivate" | "transfer";
  lockAction?: boolean;
  onClose: () => void;
  onCompleted: (message: string) => Promise<void> | void;
};

export default function QuitarVigenciaContratoModal({
  session,
  contract,
  currentCustomerId,
  currentCustomerName,
  initialAction = "deactivate",
  lockAction = false,
  onClose,
  onCompleted,
}: Props) {
  const [evaluation, setEvaluation] = useState<ContractOwnershipCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"config" | "confirm">("config");
  const [action, setAction] = useState<"deactivate" | "transfer">(initialAction);
  const [reason, setReason] = useState("");
  const [query, setQuery] = useState("");
  const [targetCustomerId, setTargetCustomerId] = useState("");
  const [results, setResults] = useState<CustomerListItem[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const data = await evaluateContractOwnershipAction(session, contract.id);
        if (!cancelled) {
          setEvaluation(data);
          setAction(initialAction);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "No se pudo evaluar el contrato");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [contract.id, initialAction, session]);

  useEffect(() => {
    let cancelled = false;
    async function search(): Promise<void> {
      const trimmed = query.trim();
      if (action !== "transfer" || trimmed.length < 2) {
        setResults([]);
        return;
      }
      setSearching(true);
      try {
        const rows = await listCustomers(session, { q: trimmed, limit: 8 });
        if (!cancelled) {
          setResults(rows.filter((row) => row.id !== currentCustomerId));
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "No se pudo buscar cliente destino");
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }
    void search();
    return () => {
      cancelled = true;
    };
  }, [action, currentCustomerId, query, session]);

  const selectedTarget = useMemo(
    () => results.find((row) => row.id === targetCustomerId) ?? null,
    [results, targetCustomerId],
  );

  function continueFlow(): void {
    if (!evaluation) return;
    if (action === "deactivate" && !evaluation.canDeactivate) {
      setError("Este contrato no puede quedar inactivo mientras tenga deuda o lecturas en proceso.");
      return;
    }
    if (action === "transfer") {
      if (!evaluation.canTransfer) {
        if (evaluation.contractStatus !== "active") {
          setError(`El contrato ${contract.contractNumber} se encuentra no vigente.`);
        } else if (evaluation.totalDebt > 0) {
          setError("No se puede traspasar el contrato porque mantiene documentos pendientes de pago.");
        } else {
          setError("No se puede traspasar el contrato porque posee lectura ingresada en el periodo abierto.");
        }
        return;
      }
      if (!targetCustomerId) {
        setError("Selecciona el nuevo titular del contrato.");
        return;
      }
    }
    setError(null);
    setStep("confirm");
  }

  async function confirmAction(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      await executeContractOwnershipAction(session, contract.id, {
        action,
        targetCustomerId: action === "transfer" ? targetCustomerId : undefined,
        reason: reason.trim() || undefined,
      });
      await onCompleted(
        action === "deactivate"
          ? `Se quitó la vigencia al contrato ${contract.contractNumber}.`
          : `Se traspasó el contrato ${contract.contractNumber} a ${selectedTarget?.nombre ?? "nuevo titular"}.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo ejecutar la acción");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={saving ? undefined : onClose} />
      <div className="relative w-full max-w-4xl rounded-3xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between bg-amber-500 px-8 py-5 text-white">
          <div>
            <h2 className="text-lg font-semibold">{initialAction === "transfer" ? "Traspaso de contrato" : "Quitar vigencia del contrato"}</h2>
            <p className="text-sm text-amber-100">{currentCustomerName} - {contract.contractNumber}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 hover:bg-white/30 disabled:opacity-60"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="space-y-5 p-6">
          {loading ? <div className="h-40 animate-pulse rounded-2xl bg-gray-100" /> : null}

          {!loading && evaluation ? (
            <>
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-gray-200 bg-white p-5">
                  <h3 className="mb-3 text-base font-semibold text-gray-800">Contrato actual</h3>
                  <div className="space-y-2 text-sm text-gray-600">
                    <p><span className="font-medium text-gray-800">Contrato:</span> {contract.contractNumber}</p>
                    <p><span className="font-medium text-gray-800">Dirección:</span> {contract.direccion || "-"}</p>
                    <p><span className="font-medium text-gray-800">Sector:</span> {contract.sector || "-"}</p>
                    <p><span className="font-medium text-gray-800">Titular:</span> {evaluation.customer.name}</p>
                  </div>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-slate-50 p-5">
                  <h3 className="mb-3 text-base font-semibold text-gray-800">Evaluación previa</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="rounded-xl bg-white px-4 py-3 border border-gray-200">
                      <div className="text-gray-500">Deuda total</div>
                      <div className={`font-semibold ${evaluation.totalDebt > 0 ? "text-red-600" : "text-emerald-600"}`}>
                        {evaluation.totalDebt > 0 ? `$${evaluation.totalDebt.toLocaleString("es-CL")}` : "Sin deuda"}
                      </div>
                    </div>
                    <div className="rounded-xl bg-white px-4 py-3 border border-gray-200">
                      <div className="text-gray-500">Lecturas en proceso</div>
                      <div className={`font-semibold ${evaluation.hasPendingReadingProcess ? "text-amber-600" : "text-emerald-600"}`}>
                        {evaluation.hasPendingReadingProcess ? "Sí" : "No"}
                      </div>
                    </div>
                    <div className="rounded-xl bg-white px-4 py-3 border border-gray-200">
                      <div className="text-gray-500">Documentos abiertos</div>
                      <div className="font-semibold text-gray-800">{evaluation.openDocuments}</div>
                    </div>
                    <div className="rounded-xl bg-white px-4 py-3 border border-gray-200">
                      <div className="text-gray-500">Compromisos activos</div>
                      <div className="font-semibold text-gray-800">{evaluation.activeInstallments}</div>
                    </div>
                  </div>
                  {evaluation.pendingReason ? (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      {evaluation.pendingReason}
                    </div>
                  ) : null}
                </div>
              </div>

              {step === "config" ? (
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="mb-4 text-base font-semibold text-gray-800">Acción</h3>
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => setAction("deactivate")}
                        disabled={lockAction}
                        className={`w-full rounded-2xl border px-4 py-4 text-left ${action === "deactivate" ? "border-amber-400 bg-amber-50" : "border-gray-200 bg-white"}`}
                      >
                        <div className="font-semibold text-gray-800">Dejar contrato inactivo</div>
                        <div className="mt-1 text-sm text-gray-600">
                          Mantiene el historial, pero solo se permite sin deuda ni lecturas pendientes.
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setAction("transfer")}
                        disabled={lockAction}
                        className={`w-full rounded-2xl border px-4 py-4 text-left ${action === "transfer" ? "border-blue-400 bg-blue-50" : "border-gray-200 bg-white"}`}
                      >
                        <div className="font-semibold text-gray-800">Traspasar contrato a otro usuario</div>
                        <div className="mt-1 text-sm text-gray-600">
                          Cambia el titular solo si el contrato está vigente, sin deuda y sin lecturas pendientes.
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 p-5">
                    <h3 className="mb-4 text-base font-semibold text-gray-800">Detalle</h3>
                    {action === "transfer" ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={query}
                          onChange={(event) => {
                            setQuery(event.target.value);
                            setTargetCustomerId("");
                          }}
                          placeholder="Buscar cliente por nombre o RUT"
                          className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-blue-400"
                        />
                        <div className="max-h-48 space-y-2 overflow-auto">
                          {searching ? <div className="text-sm text-gray-500">Buscando clientes...</div> : null}
                          {!searching && results.length === 0 ? (
                            <div className="text-sm text-gray-500">Escribe al menos 2 caracteres para buscar el nuevo titular.</div>
                          ) : null}
                          {results.map((row) => (
                            <button
                              key={row.id}
                              type="button"
                              onClick={() => setTargetCustomerId(row.id)}
                              className={`w-full rounded-xl border px-4 py-3 text-left ${targetCustomerId === row.id ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-white"}`}
                            >
                              <div className="font-medium text-gray-800">{row.nombre}</div>
                              <div className="text-sm text-gray-500">{row.rut} - {row.direccion || "-"}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                        El contrato quedará inactivo y conservará su historial. No podrá seguir operando hasta que se reactive.
                      </div>
                    )}

                    <div className="mt-4">
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Motivo</label>
                      <textarea
                        value={reason}
                        onChange={(event) => setReason(event.target.value)}
                        rows={3}
                        className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-amber-400"
                        placeholder="Motivo del cambio"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-6">
                  <h3 className="text-base font-semibold text-gray-800">Confirmación</h3>
                  <div className="mt-3 space-y-2 text-sm text-gray-600">
                    {action === "deactivate" ? (
                      <>
                        <p>Se quitará la vigencia al contrato <span className="font-semibold text-gray-800">{contract.contractNumber}</span>.</p>
                        <p>El contrato quedará en estado inactivo y conservará todo su historial.</p>
                      </>
                    ) : (
                      <>
                        <p>Se traspasará el contrato <span className="font-semibold text-gray-800">{contract.contractNumber}</span> desde <span className="font-semibold text-gray-800">{evaluation.customer.name}</span> a <span className="font-semibold text-gray-800">{selectedTarget?.nombre ?? "-"}</span>.</p>
                        <p>El cambio quedará registrado en el historial de vigencia y titularidad.</p>
                      </>
                    )}
                    {reason.trim() ? <p><span className="font-medium text-gray-800">Motivo:</span> {reason.trim()}</p> : null}
                  </div>
                </div>
              )}
            </>
          ) : null}

          {error ? <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div> : null}

          <div className="flex justify-between">
            <div>
              {step === "confirm" ? (
                <button
                  type="button"
                  onClick={() => setStep("config")}
                  disabled={saving}
                  className="rounded-xl bg-gray-100 px-5 py-3 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-60"
                >
                  Volver
                </button>
              ) : null}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="rounded-xl bg-gray-100 px-5 py-3 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-60"
              >
                Cancelar
              </button>
              {step === "config" ? (
                <button
                  type="button"
                  onClick={continueFlow}
                  disabled={loading}
                  className="rounded-xl bg-amber-500 px-5 py-3 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
                >
                  Continuar
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void confirmAction()}
                  disabled={saving}
                  className="rounded-xl bg-teal-600 px-5 py-3 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-60"
                >
                  {saving ? "Guardando..." : "Confirmar"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
